import { useEffect, useState } from "react";
import { Microscope, Loader2, Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_REC = { screening_type: "", date: new Date().toISOString().slice(0, 10), result: "", next_due: "", provider: "", dose: "", notes: "" };

function dueChip(latest) {
    if (!latest) return <span className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Never done</span>;
    if (!latest.next_due) return <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle2 className="inline h-3 w-3 mr-0.5" />Done</span>;
    const today = new Date().toISOString().slice(0, 10);
    const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    if (latest.next_due < today) return <span className="rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-600"><AlertTriangle className="inline h-3 w-3 mr-0.5" />Overdue</span>;
    if (latest.next_due <= soon) return <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Due soon</span>;
    return <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Up to date</span>;
}

export default function ScreeningsTab({ patientId }) {
    const [data, setData] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_REC);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/screenings`);
            setData(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };
    useEffect(() => { load(); }, [patientId]);

    const save = async (e) => {
        e.preventDefault();
        if (!form.screening_type) { toast.error("Select a screening type"); return; }
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/screenings`, form);
            toast.success("Screening recorded");
            setShowAdd(false);
            setForm(EMPTY_REC);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete ${data.types[r.screening_type]} record of ${r.date}?`)) return;
        try {
            await api.delete(`/screenings/${r.id}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>;

    return (
        <div className="space-y-4" data-testid="screenings-tab">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Microscope className="h-4 w-4 text-[#E06D1F]" /> Preventive Screenings</h3>
                <Button size="sm" data-testid="add-screening-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> Record Screening</Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="screening-cards">
                {Object.entries(data.types).map(([key, label]) => {
                    const latest = data.latest[key];
                    return (
                        <div key={key} data-testid={`screening-card-${key}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-sm font-semibold text-slate-900">{label}</p>
                                {dueChip(latest)}
                            </div>
                            {latest ? (
                                <p className="text-xs text-slate-500 mt-1.5">
                                    Last: <b>{latest.date}</b>{latest.result ? ` · ${latest.result}` : ""}{latest.dose ? ` · ${latest.dose}` : ""}
                                    {latest.next_due ? <><br />Next due: <b>{latest.next_due}</b></> : ""}
                                </p>
                            ) : (
                                <p className="text-xs text-slate-400 mt-1.5">No record on file</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {data.records.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="screening-history-card">
                    <div className="px-5 py-3 border-b border-slate-100"><h4 className="text-sm font-semibold text-slate-700">History</h4></div>
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                            <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Screening</th><th className="px-4 py-2.5">Result</th><th className="px-4 py-2.5 hidden sm:table-cell">Next Due</th><th className="px-4 py-2.5 hidden md:table-cell">Provider</th><th className="px-4 py-2.5"></th>
                        </tr></thead>
                        <tbody>
                            {data.records.map((r) => (
                                <tr key={r.id} data-testid={`screening-row-${r.id}`} className="border-t border-slate-100">
                                    <td className="px-4 py-2.5 text-slate-700">{r.date}</td>
                                    <td className="px-4 py-2.5 font-medium text-slate-900">{data.types[r.screening_type] || r.screening_type}{r.dose ? ` (${r.dose})` : ""}</td>
                                    <td className="px-4 py-2.5 text-slate-600 max-w-[220px] truncate">{r.result || "—"}</td>
                                    <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">{r.next_due || "—"}</td>
                                    <td className="px-4 py-2.5 hidden md:table-cell text-slate-600">{r.provider || "—"}</td>
                                    <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" data-testid={`screening-delete-${r.id}`} onClick={() => remove(r)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md" data-testid="screening-add-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Record Screening</DialogTitle>
                        <DialogDescription>Set "next due" to surface this patient in dashboard recall alerts.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Screening Type *</Label>
                            <select data-testid="screening-type-select" value={form.screening_type} onChange={(e) => setForm({ ...form, screening_type: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                <option value="">Select</option>
                                {Object.entries(data.types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="screening-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Next Due</Label><Input data-testid="screening-next-input" type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Result</Label><Input data-testid="screening-result-input" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} placeholder="NILM / ASC-US / BI-RADS 2 / Normal..." /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Provider / Lab</Label><Input data-testid="screening-provider-input" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Dose (HPV vaccine)</Label><Input data-testid="screening-dose-input" value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} placeholder="Dose 1 / 2 / 3" /></div>
                        </div>
                        <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="screening-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Colposcopy site, biopsy histopath..." /></div>
                        <Button data-testid="screening-submit-btn" type="submit" disabled={saving} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Record</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
